import React, { useMemo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, ChevronRight, Music4, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { playClickGenre } from "@/lib/beep";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";

/**
 * Cascading Macro > Sub > Micro genre picker.
 *
 * The final selected micro-genre string is the same value we ship to the
 * backend as the `genre` field on /api/analyze. The taxonomy is fetched
 * from /api/genres (returned already localised by X-Lang).
 */
export default function GenreSelector({
  taxonomy = [],
  microValue,
  onMicroChange,
}) {
  const { t } = useTranslation();

  // Find macro/sub currently containing microValue
  const path = useMemo(() => {
    for (const macro of taxonomy) {
      for (const sub of macro.subcategories) {
        for (const m of sub.microgenres) {
          if (m.id === microValue)
            return { macroId: macro.id, subId: sub.id };
        }
      }
    }
    return { macroId: taxonomy[0]?.id || null, subId: taxonomy[0]?.subcategories?.[0]?.id || null };
  }, [taxonomy, microValue]);

  const macro = taxonomy.find((m) => m.id === path.macroId) || taxonomy[0];
  const sub =
    macro?.subcategories.find((s) => s.id === path.subId) ||
    macro?.subcategories[0];

  // If microValue is empty but taxonomy is loaded, pre-fill with the first micro
  useEffect(() => {
    if (!microValue && sub && sub.microgenres[0]) {
      onMicroChange(sub.microgenres[0].id);
    }
  }, [taxonomy.length]);

  const onMacroChange = (macroId) => {
    const next = taxonomy.find((m) => m.id === macroId);
    if (!next) return;
    const firstSub = next.subcategories[0];
    const firstMicro = firstSub?.microgenres[0]?.id;
    if (firstMicro) onMicroChange(firstMicro);
    playClickGenre();
  };

  const onSubChange = (subId) => {
    const nextSub = macro?.subcategories.find((s) => s.id === subId);
    if (!nextSub) return;
    const firstMicro = nextSub.microgenres[0]?.id;
    if (firstMicro) onMicroChange(firstMicro);
    playClickGenre();
  };

  // Refs so the breadcrumb chip can imperatively open the matching dropdown.
  // Must be declared BEFORE any conditional early-return to respect the
  // React "Rules of Hooks" (same call order on every render).
  const macroRef = useRef(null);
  const subRef = useRef(null);
  const microRef = useRef(null);

  const openMacro = () => macroRef.current?.click();
  const openSub = () => subRef.current?.click();
  const openMicro = () => microRef.current?.click();

  if (!taxonomy.length) return null;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5" data-testid="genre-selector">
      <div>
        <label className="tiny-caps mb-1.5 block">{t("home.macro_label")}</label>
        <SearchableCombobox
          triggerRef={macroRef}
          items={taxonomy.map((m) => ({ id: m.id, label: m.label }))}
          value={macro?.id || ""}
          onChange={onMacroChange}
          placeholder={t("home.macro_placeholder")}
          searchPlaceholder={t("home.macro_search_placeholder")}
          emptyLabel={t("home.macro_search_empty")}
          testid="genre-macro-select"
          optionTestidPrefix="macro-option"
          highlight={false}
        />
      </div>

      <div>
        <label className="tiny-caps mb-1.5 block">{t("home.sub_label")}</label>
        <SearchableCombobox
          triggerRef={subRef}
          items={(macro?.subcategories || []).map((s) => ({ id: s.id, label: s.label }))}
          value={sub?.id || ""}
          onChange={onSubChange}
          placeholder={t("home.sub_placeholder")}
          searchPlaceholder={t("home.sub_search_placeholder")}
          emptyLabel={t("home.sub_search_empty")}
          testid="genre-sub-select"
          optionTestidPrefix="sub-option"
          highlight={false}
        />
      </div>

      <div>
        <label className="tiny-caps mb-1.5 block">{t("home.micro_label")}</label>
        <SearchableCombobox
          triggerRef={microRef}
          items={sub?.microgenres || []}
          value={microValue || ""}
          onChange={onMicroChange}
          placeholder={t("home.micro_placeholder")}
          searchPlaceholder={t("home.micro_search_placeholder")}
          emptyLabel={t("home.micro_search_empty")}
          testid="genre-micro-select"
          optionTestidPrefix="micro-option"
          highlight={true}
        />
      </div>
      <GenreBreadcrumb
        macroLabel={macro?.label}
        subLabel={sub?.label}
        microLabel={sub?.microgenres.find((m) => m.id === microValue)?.label}
        onMacroClick={openMacro}
        onSubClick={openSub}
        onMicroClick={openMicro}
        onReset={() => {
          const firstMacro = taxonomy[0];
          const firstSub = firstMacro?.subcategories?.[0];
          const firstMicro = firstSub?.microgenres?.[0]?.id;
          if (firstMicro) onMicroChange(firstMicro);
        }}
      />
    </div>
  );
}

/** Breadcrumb-like chip that shows the currently selected Macro › Sub › Micro
 *  path and lets the user reset the selection back to the first entry with
 *  a single click. Purely presentational; state stays in GenreSelector. */
function GenreBreadcrumb({
  macroLabel,
  subLabel,
  microLabel,
  onMacroClick,
  onSubClick,
  onMicroClick,
  onReset,
}) {
  const { t } = useTranslation();
  if (!macroLabel || !subLabel || !microLabel) return null;
  const seg =
    "truncate rounded px-1 -mx-1 hover:bg-white/10 hover:text-white transition-colors focus:outline-none focus:ring-1 focus:ring-[color:var(--brand)]/50 cursor-pointer";
  return (
    <div className="col-span-full mt-1 flex items-center justify-between gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5">
      <div
        className="flex items-center gap-1.5 text-xs text-[color:var(--fg-secondary)] min-w-0"
        data-testid="genre-breadcrumb"
      >
        <Music4 size={11} className="text-[color:var(--brand)] shrink-0" />
        <button
          type="button"
          onClick={onMacroClick}
          className={seg}
          data-testid="breadcrumb-macro"
          title={t("home.breadcrumb_click_hint")}
        >
          {macroLabel}
        </button>
        <ChevronRight size={11} className="opacity-40 shrink-0" />
        <button
          type="button"
          onClick={onSubClick}
          className={seg}
          data-testid="breadcrumb-sub"
          title={t("home.breadcrumb_click_hint")}
        >
          {subLabel}
        </button>
        <ChevronRight size={11} className="opacity-40 shrink-0" />
        <button
          type="button"
          onClick={onMicroClick}
          className={`${seg} text-white font-medium`}
          data-testid="breadcrumb-micro"
          title={t("home.breadcrumb_click_hint")}
        >
          {microLabel}
        </button>
      </div>
      <button
        type="button"
        onClick={onReset}
        className="flex items-center gap-1 text-[10px] tiny-caps text-[color:var(--fg-muted)] hover:text-white transition-colors shrink-0"
        data-testid="genre-breadcrumb-reset"
        title={t("home.breadcrumb_reset_hint")}
      >
        <RotateCcw size={10} />
        {t("home.breadcrumb_reset")}
      </button>
    </div>
  );
}

/** Combobox with type-ahead search · used for Macro, Sub and Micro dropdowns
 *  so the user can search inside each level of the taxonomy. */
function SearchableCombobox({
  triggerRef,
  items,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  testid,
  optionTestidPrefix,
  highlight = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = items.find((it) => it.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex items-center justify-between w-full h-11 rounded-md border px-3 text-sm text-white touch-manipulation transition-colors",
            highlight
              ? "bg-white/5 border-[color:var(--brand)]/40 hover:border-[color:var(--brand)]"
              : "bg-white/5 border-white/10 hover:border-white/20",
            "focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/40"
          )}
          data-testid={testid}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-[color:var(--bg-elev)] border-white/10 text-white w-[--radix-popover-trigger-width] min-w-[220px] z-50 max-h-[60vh]"
        align="start"
        sideOffset={4}
      >
        <Command className="bg-transparent">
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-10 text-sm text-white placeholder:text-white/40"
            data-testid={`${optionTestidPrefix}-search-input`}
          />
          <CommandList className="max-h-64">
            <CommandEmpty className="py-6 text-center text-xs text-white/50">
              {emptyLabel}
            </CommandEmpty>
            <CommandGroup>
              {items.map((it) => (
                <CommandItem
                  key={it.id}
                  value={it.label}
                  onSelect={() => {
                    onChange(it.id);
                    playClickGenre();
                    setOpen(false);
                  }}
                  className="text-white aria-selected:bg-white/10 aria-selected:text-white cursor-pointer"
                  data-testid={`${optionTestidPrefix}-${String(it.id).replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === it.id ? "opacity-100 text-[color:var(--brand)]" : "opacity-0"
                    )}
                  />
                  {it.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
